/**
 * Analisis de la fuente de un video.
 *
 * Los videos del proyecto pueden venir de dos sitios y no se reproducen igual:
 *
 *  - Archivo propio (mp4, webm): se sirve con el elemento video del navegador.
 *    Es la opcion buena, porque permite arrancar con sonido aprovechando el
 *    gesto de inicio del recorrido, no depende de terceros y no envia ningun
 *    dato fuera.
 *
 *  - YouTube: hay que incrustarlo en un iframe. Funciona, pero con dos
 *    limitaciones que conviene tener presentes: solo se puede arrancar de forma
 *    automatica en silencio, y supone una conexion a un tercero. Se usa el
 *    dominio sin cookies para reducir el rastreo.
 *
 * El analisis vive en el paquete compartido para que el panel de administracion
 * pueda avisar del tipo de fuente al pegar una direccion, con el mismo criterio
 * que aplica el reproductor.
 */

export type FuenteVideo =
  | { tipo: 'archivo'; url: string }
  | { tipo: 'youtube'; id: string; vertical: boolean }
  | null;

/** Identificador de YouTube: once caracteres de un alfabeto conocido. */
const ID_YOUTUBE = /^[\w-]{11}$/;

const EXTENSIONES_VIDEO = ['.mp4', '.webm', '.ogv', '.ogg', '.mov', '.m3u8'];

/**
 * Determina como hay que reproducir una direccion.
 *
 * Devuelve null si la direccion esta vacia o no es utilizable, para que la
 * interfaz muestre el aviso de "sin video" en lugar de un hueco roto.
 */
export function analizarFuenteVideo(direccion: string | null | undefined): FuenteVideo {
  if (!direccion) return null;

  const texto = direccion.trim();
  if (texto === '') return null;

  let url: URL;
  try {
    url = new URL(texto);
  } catch {
    // Puede ser una ruta relativa a un archivo servido por la propia aplicacion.
    return texto.startsWith('/') ? { tipo: 'archivo', url: texto } : null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const anfitrion = url.hostname.toLowerCase().replace(/^www\./, '');

  if (anfitrion === 'youtu.be') {
    const id = url.pathname.slice(1).split('/')[0] ?? '';
    return ID_YOUTUBE.test(id) ? { tipo: 'youtube', id, vertical: false } : null;
  }

  if (anfitrion === 'youtube.com' || anfitrion === 'm.youtube.com' || anfitrion === 'youtube-nocookie.com') {
    const partes = url.pathname.split('/').filter(Boolean);

    // Formato corto vertical: /shorts/ID
    if (partes[0] === 'shorts' && partes[1]) {
      return ID_YOUTUBE.test(partes[1])
        ? { tipo: 'youtube', id: partes[1], vertical: true }
        : null;
    }

    // Incrustado directo: /embed/ID
    if (partes[0] === 'embed' && partes[1]) {
      return ID_YOUTUBE.test(partes[1])
        ? { tipo: 'youtube', id: partes[1], vertical: false }
        : null;
    }

    // Formato habitual: /watch?v=ID
    const v = url.searchParams.get('v');
    if (v && ID_YOUTUBE.test(v)) return { tipo: 'youtube', id: v, vertical: false };

    return null;
  }

  const ruta = url.pathname.toLowerCase();
  if (EXTENSIONES_VIDEO.some((extension) => ruta.endsWith(extension))) {
    return { tipo: 'archivo', url: texto };
  }

  // Direccion desconocida: se trata como archivo y que decida el navegador.
  // Es preferible a descartarla, porque hay servidores que sirven video sin
  // extension en la ruta.
  return { tipo: 'archivo', url: texto };
}

/**
 * Construye la direccion de incrustacion de YouTube.
 *
 * Se usa youtube-nocookie.com, que evita las cookies de seguimiento mientras no
 * se reproduzca el video. No elimina la conexion con el tercero, asi que este
 * tratamiento tiene que estar declarado en la politica de privacidad.
 */
export function urlIncrustadaYoutube(
  id: string,
  opciones: { automatico?: boolean; idioma?: string; origen?: string } = {},
): string {
  const url = new URL(`https://www.youtube-nocookie.com/embed/${id}`);

  if (opciones.automatico) {
    url.searchParams.set('autoplay', '1');
    // Sin mute, ningun navegador permite el arranque automatico dentro de un
    // iframe. Es la limitacion principal frente a un archivo propio.
    url.searchParams.set('mute', '1');
  }
  url.searchParams.set('playsinline', '1');
  url.searchParams.set('rel', '0');
  url.searchParams.set('modestbranding', '1');
  if (opciones.idioma) url.searchParams.set('hl', opciones.idioma);

  // Permite pedirle al reproductor que arranque enviandole un mensaje, sin
  // cargar el script de YouTube. Hace falta porque el arranque automatico por
  // parametro se pierde si el iframe se monta mientras la ventana modal
  // todavia se esta abriendo, que es justo lo que pasa al llegar a un punto.
  if (opciones.origen) {
    url.searchParams.set('enablejsapi', '1');
    url.searchParams.set('origin', opciones.origen);
  }

  return url.toString();
}

/**
 * Mensaje que entiende el reproductor incrustado de YouTube.
 *
 * Se envia con postMessage al iframe. Es la via oficial de la IFrame API sin
 * tener que cargar su script, que ademas seria una conexion mas con un tercero.
 */
export function ordenYoutube(funcion: 'playVideo' | 'pauseVideo' | 'unMute'): string {
  return JSON.stringify({ event: 'command', func: funcion, args: [] });
}
